import * as B from '@blueprintjs/core';
import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiAlertCountsByFeatureId, ApiNoteCountsByFeatureId} from 'app/modules/Remote/FeatureCollection';
import {getMultiFeaturePropertyParts} from 'app/utils/multiFeaturePropertyUtils';

import cs from './styles.module.scss';

const NAME_TRUNCATE_THRESHOLD = 25;

export function MultiLocationPanel({
  features,
  selectedFeature,
  projectId,
  featureCollectionId,
  onSelect,
  hoverFeatureById,
}: {
  features: I.ImmutableOf<ApiFeature[]>;
  selectedFeature: I.ImmutableOf<ApiFeature>;
  projectId: string;
  featureCollectionId: number;
  onSelect: (lensId: string) => void;
  hoverFeatureById: (featureId: number | null) => void;
}) {
  const [alertFilter, setAlertFilter] = React.useState('all');
  const [reportFilter, setReportFilter] = React.useState('all');
  const [notesFilterLabel, setNotesFilterLabel] = React.useState<string>('All time');

  const multiFeaturePropertyParts = React.useMemo(
    () => getMultiFeaturePropertyParts(features, selectedFeature),
    [features, selectedFeature]
  );

  const {data: userNoteCounts = {}} = useQuery({
    queryKey: ['userNoteCountsByFeatureId', projectId, featureCollectionId],
    queryFn: async () => {
      const response = await api.featureCollections.fetchNoteCountsByFeatureId(
        projectId,
        featureCollectionId,
        'user'
      );
      return response.get('data');
    },
    select: (data) => data.toJS() as ApiNoteCountsByFeatureId,
  });

  const {data: alertCountsByFeatureId = {}} = useQuery({
    queryKey: ['alertCountsByFeatureId', projectId, featureCollectionId],
    queryFn: async () => {
      const response = await api.featureCollections.fetchAlertCountsByFeatureId(
        projectId,
        featureCollectionId
      );
      return response.get('data');
    },
    select: (data) => data.toJS() as ApiAlertCountsByFeatureId,
  });

  const {data: reportCountsByFeatureId = {}} = useQuery({
    queryKey: ['reportCountsByFeatureId', projectId, featureCollectionId],
    queryFn: async () => {
      const response = await api.featureCollections.fetchReportCountsByFeatureId(
        projectId,
        featureCollectionId
      );
      return response.get('data');
    },
    select: (data) => data.toJS() as Record<string, number>,
  });
  const noteYears = React.useMemo(() => {
    const currYear = new Date().getFullYear();
    const lastYear = currYear - 1;
    return [currYear, lastYear];
  }, []);

  const noteCountByFeatureId = React.useMemo(() => {
    const counts: Record<number, number> = {};
    for (const [featureId, yearCounts] of Object.entries(userNoteCounts)) {
      const count =
        notesFilterLabel === 'All time'
          ? (yearCounts['all'] ?? 0)
          : (yearCounts[notesFilterLabel] ?? 0);
      counts[Number(featureId)] = count;
    }
    return counts;
  }, [userNoteCounts, notesFilterLabel]);

  const alertCountByFeatureId = React.useMemo(() => {
    const counts: Record<number, number> = {};
    for (const [featureId, bucketed] of Object.entries(alertCountsByFeatureId)) {
      counts[Number(featureId)] = bucketed['all'] ?? 0;
    }
    return counts;
  }, [alertCountsByFeatureId]);

  const filteredParts = React.useMemo(
    () =>
      multiFeaturePropertyParts.filter(({feature}) => {
        const featureId = feature.get('id');
        if (notesFilterLabel !== 'All time') {
          if ((noteCountByFeatureId[featureId] ?? 0) === 0) return false;
        }
        if (alertFilter !== 'all') {
          const count = alertCountByFeatureId[featureId] ?? 0;
          if (alertFilter === 'has' && count === 0) return false;
          if (alertFilter === 'none' && count > 0) return false;
        }
        if (reportFilter !== 'all') {
          const count = reportCountsByFeatureId[String(featureId)] ?? 0;
          if (reportFilter === 'has' && count === 0) return false;
          if (reportFilter === 'none' && count > 0) return false;
        }
        return true;
      }),
    [
      multiFeaturePropertyParts,
      notesFilterLabel,
      noteCountByFeatureId,
      alertFilter,
      alertCountByFeatureId,
      reportFilter,
      reportCountsByFeatureId,
    ]
  );

  const propertyName = selectedFeature.getIn(['properties', 'name']) as string;

  return (
    <div className={cs.multiLocationPanel}>
      <div className={cs.multiLocationHeader}>
        <span className={cs.multiLocationName}>
          <strong>{propertyName}</strong> - {multiFeaturePropertyParts.length} locations
          {filteredParts.length < multiFeaturePropertyParts.length &&
            ` (${filteredParts.length} shown)`}
        </span>
      </div>
      <div className={cs.multiLocationDescription}>
        Select a location in the table or map to monitor.
      </div>
      <div className={cs.multiLocationTableWrapper}>
        <table className={cs.multiLocationTable}>
          <thead>
            <tr>
              <th></th>
              <th className={cs.multiLocationAlertsCol} style={{textAlign: 'center'}}>
                <B.Popover
                  minimal
                  placement="bottom-start"
                  content={
                    <B.Menu>
                      <B.MenuItem
                        text="All"
                        icon={alertFilter === 'all' ? 'tick' : 'blank'}
                        onClick={() => setAlertFilter('all')}
                      />
                      <B.MenuItem
                        text="Has alerts"
                        icon={alertFilter === 'has' ? 'tick' : 'blank'}
                        onClick={() => setAlertFilter('has')}
                      />
                      <B.MenuItem
                        text="No alerts"
                        icon={alertFilter === 'none' ? 'tick' : 'blank'}
                        onClick={() => setAlertFilter('none')}
                      />
                    </B.Menu>
                  }
                >
                  <B.Tooltip content="Filter by alerts">
                    <B.Button minimal icon="warning-sign" outlined active={alertFilter !== 'all'} />
                  </B.Tooltip>
                </B.Popover>
              </th>
              <th className={cs.multiLocationNotesCol} style={{textAlign: 'right'}}>
                <B.Popover
                  minimal
                  placement="bottom-start"
                  content={
                    <B.Menu>
                      {noteYears.map((y) => (
                        <B.MenuItem
                          key={y}
                          text={String(y)}
                          icon={notesFilterLabel === String(y) ? 'tick' : 'blank'}
                          onClick={() => setNotesFilterLabel(String(y))}
                        />
                      ))}
                      <B.MenuDivider />
                      <B.MenuItem
                        text="All time"
                        icon={notesFilterLabel === 'All time' ? 'tick' : 'blank'}
                        onClick={() => setNotesFilterLabel('All time')}
                      />
                    </B.Menu>
                  }
                >
                  <B.Tooltip content="Filter by notes">
                    <B.Button
                      minimal
                      icon="annotation"
                      outlined
                      active={notesFilterLabel !== 'All time'}
                    />
                  </B.Tooltip>
                </B.Popover>
              </th>
              <th className={cs.multiLocationReportsCol} style={{textAlign: 'right'}}>
                <B.Popover
                  minimal
                  placement="bottom-start"
                  content={
                    <B.Menu>
                      <B.MenuItem
                        text="All"
                        icon={reportFilter === 'all' ? 'tick' : 'blank'}
                        onClick={() => setReportFilter('all')}
                      />
                      <B.MenuItem
                        text="Has reports"
                        icon={reportFilter === 'has' ? 'tick' : 'blank'}
                        onClick={() => setReportFilter('has')}
                      />
                      <B.MenuItem
                        text="No reports"
                        icon={reportFilter === 'none' ? 'tick' : 'blank'}
                        onClick={() => setReportFilter('none')}
                      />
                    </B.Menu>
                  }
                >
                  <B.Tooltip content="Filter by reports">
                    <B.Button minimal icon="document" outlined active={reportFilter !== 'all'} />
                  </B.Tooltip>
                </B.Popover>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredParts.map(({partName, feature}) => {
              const featureId = feature.get('id');
              return (
                <tr
                  key={featureId}
                  onClick={() => onSelect(feature.getIn(['properties', 'lensId'])!)}
                  onMouseEnter={() => hoverFeatureById(featureId)}
                  onMouseLeave={() => hoverFeatureById(null)}
                >
                  <td className={cs.multiLocationLocationCell}>
                    {(() => {
                      const name = partName != null ? String(partName) : '';
                      if (!name) return <em>Unnamed location</em>;
                      if (name.length > NAME_TRUNCATE_THRESHOLD)
                        return <B.Tooltip content={name}>{name}</B.Tooltip>;
                      return name;
                    })()}
                  </td>
                  <td className={cs.multiLocationAlertsCol}>
                    {alertCountByFeatureId[featureId] ?? 0}
                  </td>
                  <td className={cs.multiLocationNotesCol}>
                    {noteCountByFeatureId[featureId] ?? 0}
                  </td>
                  <td className={cs.multiLocationReportsCol}>
                    {reportCountsByFeatureId[String(featureId)] ?? 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={cs.multiLocationFooter}>
        Want to monitor all these locations at once?{' '}
        <a
          href="https://lens.tech/support/article/176-lens-dashboard"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about dashboards.
        </a>
      </div>
    </div>
  );
}
